<template>
  <div v-if="show" class="fixed inset-0 z-[50] flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white rounded-t-2xl w-full max-w-md h-[80vh] flex flex-col relative">
      <!-- Chat Header -->
      <div class="bg-primary-500 text-white p-4 rounded-t-2xl flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-white rounded-full overflow-hidden">
            <ImgComponent avatar :src="riderData.avatar || 'johndoe'" />
          </div>
          <div>
            <div class="font-semibold">{{ riderData.name || 'Votre livreur' }}</div>
            <div class="text-xs opacity-80">{{ onlineStatus }}</div>
          </div>
        </div>
        <button
          @click="closeChat"
          class="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Chat Container -->
      <div class="flex-1 overflow-hidden">
        <ChatContainer
          v-if="chatConversationId"
          :conversation-id="chatConversationId"
          :show-header="false"
          placeholder="Écrivez un message à votre livreur..."
          @message-sent="handleMessageSent"
          @close="closeChat"
        />
        <div v-else class="h-full flex items-center justify-center text-gray-500">
          Chargement de la conversation...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { ChatContainer, useChat } from '../index.js'
import { chatService } from '../services/ChatService.js'
import Events from '@/events/events'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'

export default {
  name: 'ChatModal',
  components: {
    ChatContainer,
    ImgComponent
  },
  props: {
    orderData: {
      type: Object,
      default: () => ({})
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'message-sent'],
  setup(props, { emit }) {
    // État local
    const chatConversationId = ref('')

    // Initialiser le composable de chat
    const chat = useChat({
      config: {
        fetchActions: {
          sendMessage: 'chat/sendRiderMessage',
          fetchMessages: 'chat/fetchRiderMessages',
          markAsRead: 'chat/markRiderMessageAsRead'
        }
      }
    })

    // Computed
    const riderData = computed(() => ({
      id: props.orderData?.riderId || `rider_${props.orderData?.orderId || 'unknown'}`,
      name: props.orderData?.riderLabel || 'Votre livreur',
      avatar: props.orderData?.riderAvatar || 'johndoe',
      role: 'rider'
    }))

    const onlineStatus = computed(() => {
      return 'En ligne' // Simulation pour le développement
    })

    // Méthodes
    const openChatWithRider = async () => {
      try {
        // Utiliser l'ID de conversation fourni ou en créer un nouveau
        if (props.orderData?.conversationId) {
          chatConversationId.value = props.orderData.conversationId
        } else if (!chatConversationId.value) {
          const conversation = await chat.createConversation([riderData.value], 'direct', {
            orderId: props.orderData?.orderId,
            type: props.orderData?.chatType || 'rider-chat',
            createdAt: new Date().toISOString()
          })

          if (conversation.id) {
            chatConversationId.value = conversation.id
          }
        }

        // Rejoindre la conversation
        if (chatConversationId.value) {
          await chat.joinConversation(chatConversationId.value)
        }

      } catch (error) {
        console.error('Failed to open chat:', error)
        // Ne pas bloquer l'ouverture du modal même si la conversation échoue
      }
    }

    const closeChat = () => {
      emit('close')
      if (chatConversationId.value) {
        chat.leaveConversation(chatConversationId.value)
      }
    }

    /**
     * @param {any} message
     */
    const handleMessageSent = (message) => {
      console.log('Message sent to rider:', message)
      emit('message-sent', message)
    }

    // Cycle de vie
    onMounted(() => {
      // Simuler un utilisateur connecté pour le chat
      if (!chatService.currentUser) {
        Events.dispatch('USER_CONNECTED', {
          id: 'demo_user_current',
          name: 'Demo User',
          avatar: 'demo',
          role: 'user'
        })
      }

      // Ouvrir le chat si le modal est visible
      if (props.show) {
        openChatWithRider()
      }
    })

    // Watcher pour ouvrir le chat quand le modal devient visible
    watch(() => props.show, (newValue) => {
      if (newValue && !chatConversationId.value) {
        openChatWithRider()
      }
    })

    return {
      chatConversationId,
      riderData,
      onlineStatus,
      openChatWithRider,
      closeChat,
      handleMessageSent
    }
  }
}
</script>

<style scoped>
/* Styles pour le modal */
.fixed {
  position: fixed;
}

.inset-0 {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.z-50 {
  z-index: 50;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-center {
  justify-content: center;
}

.bg-black {
  background-color: rgb(0 0 0);
}

.bg-opacity-50 {
  background-color: rgb(0 0 0 / 0.5);
}

.bg-white {
  background-color: white;
}

.rounded-t-2xl {
  border-radius: 1rem;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
}

.w-full {
  width: 100%;
}

.max-w-md {
  max-width: 28rem;
}

.h-80vh {
  height: 80vh;
}

.flex-col {
  flex-direction: column;
}

.relative {
  position: relative;
}

.flex-1 {
  flex: 1 1 0%;
}

.overflow-hidden {
  overflow: hidden;
}

.bg-primary-500 {
  background-color: rgb(59 130 246);
}

.text-white {
  color: white;
}

.p-4 {
  padding: 1rem;
}

.gap-3 {
  gap: 0.75rem;
}

.w-10 {
  width: 2.5rem;
}

.h-10 {
  height: 2.5rem;
}

.overflow-hidden {
  overflow: hidden;
}

.font-semibold {
  font-weight: 600;
}

.text-xs {
  font-size: 0.75rem;
}

.opacity-80 {
  opacity: 0.8;
}

.hover\:bg-white:hover {
  background-color: white;
}

.hover\:bg-opacity-20:hover {
  background-color: rgb(255 255 255 / 0.2);
}

.rounded-full {
  border-radius: 9999px;
}

.p-2 {
  padding: 0.5rem;
}

.transition-colors {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.w-6 {
  width: 1.5rem;
}

.h-6 {
  height: 1.5rem;
}

.text-gray-500 {
  color: rgb(107 114 128);
}

.h-full {
  height: 100%;
}

/* Animation d'entrée */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.fixed > div {
  animation: fadeIn 0.2s ease-out;
}

/* Responsive */
@media (max-width: 640px) {
  .max-w-md {
    max-width: 100%;
    margin: 0 1rem;
  }

  .h-80vh {
    height: 90vh;
  }
}
</style>